// ── usePolymarketUsFills (#1007) ─────────────────────────────────
//
// Polls the authenticated US fill-history read (GET /api/polymarket-us/fills)
// for the Recent Fills rail and the /fills popup. Same shape and cadence as
// usePolymarketRelayFills (#457, #498): state is stamped with the identity it
// was fetched under and derived back out, requests coalesce per scope, a
// failed read keeps the current scope's rows, and only a validated reply may
// replace them (including with an authoritative []).
//
// One addition: the identity includes the auth session epoch, the same triple
// sendUsBrowserRequest checks at send time, and it is compared again when the
// reply ARRIVES. A reply sent before a logout therefore cannot land after a
// same-user re-login, and any scope change (logout, org switch, epoch) clears
// the rows at once instead of carrying them into the next scope.
//
// #1467: each validated reply also goes to the tab's US fill-sound feed
// (`polledFillSound`), which keeps the first reply of a scope silent and hands
// later new rows to the same gate Kalshi fills use. Rows never reach
// processFill: they are not Kalshi fills and must not touch Kalshi state.
//
// #1015: a hidden tab does not poll. No read starts on mount or on a tick while
// the page is hidden, and one read starts as soon as it is visible again, unless
// one started within the last poll interval (quick tab switching stays at the
// poll rate; a read in flight is also coalesced). Scope changes while hidden
// still clear the rows at once, through the identity stamp above.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { listPolymarketUsFills, type UsFill } from '@/lib/polymarketUs/fillsClient';
import { usBrowserSession } from '@/lib/polymarketUs/browserTradingClient';
import { RECENT_FILL_LIMIT } from '@/lib/fills/recentFillTape';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { feedPolledFills, type PolledFill } from '@/stores/polledFillSound';

export type UsFillsStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface UsFillsState {
  fills: UsFill[];
  status: UsFillsStatus;
  /** True only when the relay called every visible account complete. */
  complete: boolean;
  /** Sorted union of the reply's top-level and per-account reasons. */
  reasons: string[];
  /** Null until the first validated reply; then whether any US account is visible. */
  hasAccounts: boolean | null;
  lastFetchMs: number | null;
}

export interface UsePolymarketUsFillsOptions {
  enabled?: boolean;
  pollMs?: number;
  limit?: number;
}

const IDLE: UsFillsState = { fills: [], status: 'idle', complete: false, reasons: [], hasAccounts: null, lastFetchMs: null };
const DEFAULT_POLL_MS = 10_000;

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);
const identityNow = () => {
  const { user } = useAuthStore.getState();
  return `${user?.id ?? ''}:${user?.orgId ?? ''}:${getAuthSessionEpoch()}`;
};

interface Scope { identity: string }

/** A US row as the shared fill-sound gate sees it (#1467). The claim key is
 * the account and venue execution, the same in every tab. A row without a
 * proven execution time never sounds: receipt time is not substituted. A
 * disputed quantity counts as 0, below any size threshold. */
export function usFillSound(fill: UsFill): PolledFill {
  const executedAtMs = fill.executedAt === null || fill.disputed.includes('executedAt')
    ? Number.NaN : Date.parse(fill.executedAt);
  return { fillKey: [fill.accountId, fill.executionId], orderId: fill.intentId,
    count: fill.quantity === null ? 0 : Number(fill.quantity), executedAtMs };
}

export function usePolymarketUsFills(options: UsePolymarketUsFillsOptions = {}): UsFillsState & { refresh: () => Promise<void> } {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const limit = options.limit ?? RECENT_FILL_LIMIT;

  // Subscribed, not read: an epoch bump must re-render and clear the rows.
  const epoch = useSyncExternalStore(subscribeAuth, getAuthSessionEpoch);
  const who = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);
  const authed = useAuthStore((s) => s.status === 'authenticated' && !!s.token && !!s.user?.orgId);
  const identity = `${who}:${epoch}`;

  const [stamped, setStamped] = useState<UsFillsState & { identity: string }>(() => ({ ...IDLE, identity }));
  const derived: UsFillsState = stamped.identity === identity ? stamped : IDLE;
  // A read is on its way whenever the hook is live for a session (or starts the
  // moment a hidden page becomes visible): report that as loading from the first
  // render, so consumers never make an empty-history claim before it answers.
  const current: UsFillsState = derived.status === 'idle' && enabled && authed ? { ...derived, status: 'loading' } : derived;

  const stateRef = useRef(stamped);
  const scopeRef = useRef<Scope | null>(null);
  const inFlightRef = useRef<Scope | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const session = usBrowserSession();
    if (!session) return;
    if (scopeRef.current?.identity !== identity) scopeRef.current = { identity };
    const scope = scopeRef.current;
    if (inFlightRef.current === scope) return;
    inFlightRef.current = scope;
    const carry = (): UsFillsState => (stateRef.current.identity === identity ? stateRef.current : IDLE);
    // The only state-application path. Discarded whole after unmount, under a
    // newer scope, or when the live identity (incl. epoch) moved in flight.
    const apply = (next: UsFillsState): boolean => {
      if (!mountedRef.current || scopeRef.current !== scope || identityNow() !== identity) return false;
      const resolved = { ...next, identity };
      stateRef.current = resolved;
      setStamped(resolved);
      return true;
    };
    if (carry().status === 'idle') apply({ ...carry(), status: 'loading' });
    try {
      const out = await listPolymarketUsFills(limit, session);
      const lastFetchMs = Date.now();
      if (out.ok) {
        const reasons = [...new Set([...out.reasons, ...out.accounts.flatMap((a) => a.reasons)])].sort();
        const applied = apply({ fills: out.fills, status: 'ready', complete: out.complete, reasons,
          hasAccounts: out.accounts.length > 0, lastFetchMs });
        // Only a reply that landed for the live identity may sound.
        if (applied) feedPolledFills('polymarket_us', identity, out.fills.map(usFillSound));
      } else {
        // A failed read is not an empty history: keep this scope's rows.
        // Reasons described the last answer; a failed read has none of its own.
        apply({ ...carry(), status: out.code === 'US_FILLS_MALFORMED' ? 'error' : 'unavailable',
          complete: false, reasons: [], lastFetchMs });
      }
    } finally {
      if (inFlightRef.current === scope) inFlightRef.current = null;
    }
  }, [identity, limit]);

  useEffect(() => {
    if (!enabled || !authed) return;
    let stopped = false;
    let lastPollMs = Number.NEGATIVE_INFINITY;
    const poll = () => {
      if (stopped || document.hidden) return;
      lastPollMs = performance.now();
      void refresh();
    };
    const onVisibility = () => {
      if (performance.now() - lastPollMs >= pollMs) poll(); // monotonic: an elapsed check, not a timestamp
    };
    poll();
    const intervalId = window.setInterval(poll, pollMs);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, authed, pollMs, refresh]);

  return { ...current, refresh };
}
